import { useCallback, useEffect, useRef, useState } from 'react';
import { API_GET, DBRequest, useConfig } from '../../api/api';

const SEARCH_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Below this, a query matches so much of the conversation that the results are noise —
// and it would fire a request on the first keystroke of every search.
export const MIN_SEARCH_LENGTH = 2;

const mergeById = (base, incoming) => {
  const seen = new Set(base.map((m) => m.id));
  const fresh = incoming.filter((m) => m && !seen.has(m.id));
  return fresh.length === 0 ? base : [...base, ...fresh];
};

/**
 * Searches messages within one conversation.
 *
 * GET chat/conversations/{id}/messages/search?search=&perPage=&cursor=
 * Returns the same shape as the messages route: { data, meta: { hasMore, nextCursor } }.
 *
 * Results are kept separate from the conversation's own message list — a match can be
 * hundreds of messages back, and merging the two would corrupt the reading order.
 */
export const useMessageSearch = (conversationId, query) => {
  const config = useConfig();
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | searching | ready | error
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef(null);
  // Bumped whenever the query or conversation changes, so responses for a query the user
  // has already typed past are dropped instead of overwriting newer results.
  const requestRef = useRef(0);

  const term = query.trim();
  const isActive = Boolean(conversationId) && term.length >= MIN_SEARCH_LENGTH;

  const fetchPage = useCallback(
    (cursor, requestId) => {
      const params = new URLSearchParams({
        search: term,
        perPage: String(SEARCH_PAGE_SIZE),
      });
      if (cursor) params.set('cursor', cursor);

      DBRequest({
        config,
        path: `chat/conversations/${conversationId}/messages/search?${params.toString()}`,
        method: API_GET,
        onResponse: (res) => {
          if (requestRef.current !== requestId) return;
          const page = res.data || [];
          const nextCursor = res.meta?.nextCursor ?? null;

          cursorRef.current = nextCursor;
          // Never advertise more results than we have a cursor to ask for
          setHasMore(Boolean(res.meta?.hasMore) && nextCursor !== null);
          setResults((prev) => (cursor ? mergeById(prev, page) : page));
          setStatus('ready');
          setIsLoadingMore(false);
        },
        onError: (err) => {
          if (requestRef.current !== requestId) return;
          console.error('[Chat] Message search failed:', err);
          setStatus('error');
          setIsLoadingMore(false);
        },
      });
    },
    [config, conversationId, term]
  );

  // Debounced first page. fetchPage closes over the term, so this re-runs (and restarts
  // the debounce) on every keystroke.
  useEffect(() => {
    const requestId = ++requestRef.current;
    cursorRef.current = null;

    if (!config || !isActive) {
      setResults([]);
      setStatus('idle');
      setHasMore(false);
      setIsLoadingMore(false);
      return;
    }

    setStatus('searching');
    const timer = setTimeout(() => fetchPage(null, requestId), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [config, isActive, fetchPage]);

  const loadMore = useCallback(() => {
    if (status !== 'ready' || isLoadingMore || !hasMore || !cursorRef.current) return;
    setIsLoadingMore(true);
    fetchPage(cursorRef.current, requestRef.current);
  }, [status, isLoadingMore, hasMore, fetchPage]);

  return { results, status, isLoadingMore, hasMore, loadMore, isActive, term };
};
